import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { FormIds } from '../utils/enums/FormIds.enum';
import { HourSlot, InsuranceDTO } from '@utils/interfaces';
import { GenericObj, getInsuranceName, mergeObjects } from '@utils/functions';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { useOnlineBookingStore } from './onlineBookingStore';
import { InsuranceDetailsInfo } from '@components/InsurancesForm/PatientDetailsInsuranceForm/PatientDetailsInsuranceForm.interface';

export interface AppointmentTypeInfo {
  appointmentType: string;
  idTranslation?: string;
  clBeforeAnswer?: string;
  eyeExamWithUsAnswer?: boolean;
  name: string;
  isModified?: boolean;
}

export interface InsuranceInfo {
  plan: string;
  id?: string;
  other?: string;
  type?: string;
  memberId?: string;
  holder?: string;
  insuredPartyRelationship?: string;
  isModified?: boolean;
  name?: string;
  secondaryInsurance?: {
    id?: string;
    memberId?: string;
    holder?: string;
    name?: string;
    type?: string;
    other?: string;
  };
}

export enum ChooseNewSlotType {
  BOTH,
  INSURANCE,
  APPOINTMENT,
}

export interface ChooseNewSlot {
  type?: ChooseNewSlotType;
  ruleTime?: string;
  ruleUnit?: string;
}

export interface ScheduleInfo {
  day?: Date;
  doctor: string;
  hour: string;
  slotId: HourSlot;
}

export interface ExtraInfoScheduleExam {
  timer?: string;
  timeTab?: number;
  chooseNewSlot?: ChooseNewSlot;
  expired?: boolean;
}

export interface PhoneInfo {
  prefix?: string;
  number?: number | string;
  type?: 'mobile' | 'landline';
  country?: string;
}

export type CustomFieldToSend = Array<{
  question: string;
  answer: string;
  id?: string;
}>;

export interface PatientDetailsInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: PhoneInfo;
  streetAddress: string;
  city: string;
  gender?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  primaryInsuranceDetails?: InsuranceDetailsInfo;
  secondaryInsuranceDetails?: InsuranceDetailsInfo;
  storeCustomFieldsPatient?: GenericObj;
  storeCustomFieldsInsurance?: GenericObj;
  customFieldsPatient: CustomFieldToSend;
  customFieldsInsurance: CustomFieldToSend;
}

export interface ContactDetailsInfo {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phone?: PhoneInfo;
  storeCustomFieldsMarketing?: GenericObj;
  customFieldsMarketing: CustomFieldToSend;
}

export interface Patient {
  appointmentId?: string;
  appointmentType: AppointmentTypeInfo;
  insurance: InsuranceInfo;
  scheduleExam?: ScheduleInfo;
  extraInfoScheduleExam?: ExtraInfoScheduleExam;
  patientDetails: PatientDetailsInfo;
  storeCustomFieldsCustomPage: GenericObj;
  customFieldsCustomPage: CustomFieldToSend;
  reservationId?: string;
  order?: number;
  patientId?: string; // serve sul giro reschedule
  previousSlotToDisable?: ScheduleInfo;
}

export interface PatientStore {
  patients: Array<Patient | undefined>;
  // failedPatients?: Array<Patient | undefined>;
  contactsDetails: ContactDetailsInfo;
  updatePatient: (
    currPatient: number,
    sectionKey: FormIds,
    newData: any,
    insurances?: InsuranceDTO,
    previousSlotToDisable?: ScheduleInfo, // <-- optionally
  ) => void;
  deletePatient: (patientIndex: number) => void;
  setAppointmentId: (appId: string, patientIndex: number) => void;
  // setFailedPatients: (failedPatients: Array<Patient | undefined>) => void;
  showDeletePatientModal: boolean;
  updateContactDetails: (newData: ContactDetailsInfo) => void;
  updateReservationId: (
    patientIndex: number,
    newReservationId: string | undefined,
  ) => void;
  importPatientDetailsData: () => void;
  setIsModified: (
    patientIndex: number,
    type: 'insurance' | 'appointment' | 'both',
    newValue: boolean,
  ) => void;
  setPatientOrder: (index: number, val: number) => void;
  setShowDeletePatientModal: (show: boolean) => void;
  reset: () => void;
  resetScheduleExam: (currentPatient: number) => void;
  setTimerScheduleExam: (
    currentPatient: number,
    timer: string | undefined,
  ) => void;
  setTimerExpired: (currentPatient: number) => void;
  setTimeTabScheduleExam: (
    currentPatient: number,
    timeTab: number | undefined,
  ) => void;
  setChooseNewSlotScheduleExam: (
    currentPatient: number,
    chooseNewSlot: ChooseNewSlot | undefined,
  ) => void;
  getCurrentPatient: (currentPatient: number) => Patient | undefined;
  getPatients: () => Array<Patient | undefined>;
  getContactsDetails: () => ContactDetailsInfo;
  getStore: () => PatientStore;
  previousSlotToDisable?: ScheduleInfo;
}

const initialState = {
  patients: [undefined, undefined, undefined],
  contactsDetails: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    customFieldsMarketing: [],
    storeCustomFieldsMarketing: {},
  } as ContactDetailsInfo,
  showDeletePatientModal: false,
  isModifyingForUnmount: false,
  previousSlotToDisable: undefined,
};

export const usePatientStore = create(
  persist<PatientStore>(
    (set, get) => ({
      ...initialState,

      getPatients: () => get().patients,
      getContactsDetails: () => get().contactsDetails,
      getStore: () => get(),

      resetScheduleExam: currentPatient => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            patient.scheduleExam = undefined;
            if (patient.extraInfoScheduleExam?.timeTab) {
              patient.extraInfoScheduleExam.timeTab = undefined;
            }
          }
          return { ...tempState };
        });
      },

      setPatientOrder: (index: number, newOrder: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[index]) {
            const patient = tempState.patients[index];
            patient.order = newOrder;
          }
          return { ...tempState };
        });
      },

      setTimerScheduleExam: (currentPatient, timer) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.timer = timer;
              patient.extraInfoScheduleExam.expired = false;
            } else {
              patient.extraInfoScheduleExam = {
                timer: timer,
                expired: false,
              };
            }
          }
          return { ...tempState };
        });
      },

      setTimerExpired: (currentPatient: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.expired = true;
            } else {
              patient.extraInfoScheduleExam = {
                expired: true,
              };
            }
          }
          return { ...tempState };
        });
      },

      setTimeTabScheduleExam: (currentPatient, timeTab) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.timeTab = timeTab;
            } else {
              patient.extraInfoScheduleExam = {
                timeTab,
              };
            }
          }
          return { ...tempState };
        });
      },

      setAppointmentId: (appId: string, index: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          const patients = tempState.patients.map((p, i) => {
            if (i === index) {
              return {
                ...p,
                appointmentId: appId,
              } as Patient;
            } else {
              return p;
            }
          });
          return { ...tempState, patients: patients };
        });
      },

      setChooseNewSlotScheduleExam: (currentPatient, chooseNewSlot) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              if (
                patient.extraInfoScheduleExam.chooseNewSlot &&
                chooseNewSlot
              ) {
                patient.extraInfoScheduleExam.chooseNewSlot = Object.assign(
                  patient.extraInfoScheduleExam.chooseNewSlot,
                  chooseNewSlot,
                );
              } else {
                patient.extraInfoScheduleExam.chooseNewSlot = chooseNewSlot;
              }
            } else if (patient) {
              patient.extraInfoScheduleExam = {
                chooseNewSlot,
              };
            } else {
              console.log(
                '###### da capire casistica: ',
                patient,
                chooseNewSlot,
              );
            }
          }
          return { ...tempState };
        });
      },

      setIsModified: (currentPatient, type, newValue) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (type === 'appointment' && patient.appointmentType) {
              patient.appointmentType.isModified = newValue;
            } else if (type === 'insurance' && patient.insurance) {
              patient.insurance.isModified = newValue;
            } else if (patient.insurance && patient.appointmentType) {
              patient.insurance.isModified = newValue;
              patient.appointmentType.isModified = newValue;
            }
          }
          return { ...tempState };
        });
      },

      setIsModifyingForUnmount: (isMod: boolean) => {
        set(state => {
          return {
            ...state,
            isModifyingForUnmount: isMod,
          };
        });
      },

      updateContactDetails: (newData: ContactDetailsInfo) => {
        set(state => {
          console.log('#new contact details data', newData);
          return {
            ...state,
            contactsDetails: newData,
          };
        });
      },

      updateReservationId: (
        currentPatient: number,
        newReservationId: string | undefined,
      ) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            patient.reservationId = newReservationId;
          }
          return { ...tempState };
        });
      },

      updatePatient: (
        currentPatient,
        sectionKey,
        newData,
        insurances?: InsuranceDTO,
      ) => {
        // prettier-ignore
        set(state => {
          // NOSONAR
          console.log(
            '#currentPatient.update: ',
            newData,
            currentPatient,
            sectionKey,
          );
          // Caso base: ogni step passa solo il pezzo di patient da aggiornare.
          // Se il patient non esiste ancora, lo puo creare solo AppointmentType.
          if (
            !state.patients[currentPatient] &&
            sectionKey !== FormIds.AppointmentType
          ) {
            return state;
          }

          const tempState = JSON.parse(JSON.stringify(state));
          // In caso di nuovo paziente lo aggiungo direttamente.
          if (
            currentPatient < 0 ||
            currentPatient >= tempState.patients.length
          ) {
            const newPatient = { ...newData };

            return {
              ...tempState,
              patients: [...tempState.patients, newPatient], // Crea un nuovo array con il nuovo paziente
            };
          }

          // Merge base: salvo prima il payload dello step nello store.
          // Le normalizzazioni sotto lavorano sul risultato gia mergiato.
          const patientToUpdate = mergeObjects<Patient>(
            tempState.patients[currentPatient],
            newData,
          );

          //se il paziente non ha un order, lo aggiungo
          if (
            !state.patients[currentPatient] &&
            sectionKey === FormIds.AppointmentType
          ) {
            patientToUpdate.order = currentPatient;
          }
          // Da qui in poi tengo coerenti i due modelli insurance nello store:
          // - patient.insurance
          // - patientDetails.primary/secondaryInsuranceDetails
          const isInsuranceDetailsSection =
            sectionKey === FormIds.PatientDetail ||
            sectionKey === FormIds.PatientDetailPrimaryInsurance ||
            sectionKey === FormIds.PatientDetailSecondaryInsurance;
          const isInsuranceSection =
            sectionKey === FormIds.Insurance && !!newData.insurance;
          const hasInsuranceDetails =
            !!patientToUpdate.patientDetails?.primaryInsuranceDetails ||
            !!patientToUpdate.patientDetails?.secondaryInsuranceDetails;
          const hasInsuranceState =
            !!patientToUpdate.insurance?.plan ||
            !!patientToUpdate.insurance?.id;
          const shouldNormalizeInsurance =
            isInsuranceSection ||
            (isInsuranceDetailsSection &&
              (hasInsuranceDetails || hasInsuranceState));

          // Entro qui se shoulwNormalizeInsurance è true, quindi sto aggiornanto insurance
          // oppure isInsuranceDetailsSection è true e ho già dei dettagli insurance (primary o secondary) oppure ho già uno stato insurance (plan o id).
          if (shouldNormalizeInsurance) {
            const getInsuranceById = (id?: string) =>
              id
                ? insurances?.find(
                    insurance => insurance.insuranceId.toString() === id,
                  )
                : undefined;
            const getInsuranceType = (id?: string) =>
              getInsuranceById(id)?.type;
            const getHolderName = (
              details?: { firstName?: string; lastName?: string },
              fallback?: string,
            ) =>
              details?.firstName && details?.lastName
                ? `${details.firstName} ${details.lastName}`
                : fallback || '';
            const getInsuranceMemberId = (
              details?: InsuranceDetailsInfo,
              fallback?: string,
            ) =>
              details?.insuranceMemberId === StaticValues.ADD_NEW
                ? details?.insuranceMemberIdOther || fallback
                : details?.insuranceMemberId || fallback;
            const getOtherInsurance = (
              details: InsuranceDetailsInfo | undefined,
              insuranceId: string | undefined,
              fallback?: string,
            ) =>
              insuranceId === StaticValues.OTHER_OPTION
                ? details?.insuranceNameOther ||
                  details?.other ||
                  fallback ||
                  ''
                : '';
            const getPatientName = () =>
              patientToUpdate.patientDetails?.firstName &&
              patientToUpdate.patientDetails?.lastName
                ? `${patientToUpdate.patientDetails.firstName} ${patientToUpdate.patientDetails.lastName}`
                : '';
            const shouldUseHolderDetails = (details?: InsuranceDetailsInfo) =>
              details?.provideInsuranceDetails !== StaticValues.NO;

            if (isInsuranceSection) {
              // Flusso Calendar/availability o update esplicita di insurance.
              // Il payload puo contenere solo plan oppure anche carrier/memberId:
              // i valori mancanti ricadono sui dettagli primary o sullo store.
              const incomingPatientDetails =
                (newData.patientDetails as PatientDetailsInfo) || undefined;
              const incomingPrimaryInsuranceDetails =
                incomingPatientDetails?.primaryInsuranceDetails ||
                patientToUpdate.patientDetails?.primaryInsuranceDetails;
              const existingInsurance = patientToUpdate.insurance || {
                plan: '',
              };
              const primaryInsuranceId =
                newData.insurance.id ||
                incomingPrimaryInsuranceDetails?.insuranceId ||
                existingInsurance.id ||
                '';
              const primaryInsuranceOther =
                newData.insurance.other ||
                incomingPrimaryInsuranceDetails?.insuranceNameOther ||
                incomingPrimaryInsuranceDetails?.other ||
                existingInsurance.other ||
                '';
              const primaryInsuranceMemberId =
                newData.insurance.memberId ||
                incomingPrimaryInsuranceDetails?.insuranceMemberId ||
                existingInsurance.memberId;
              const insuranceType =
                getInsuranceType(primaryInsuranceId) ||
                newData.insurance.type ||
                existingInsurance.type;
              const insuredPartyRelationship =
                newData.insurance?.insuredPartyRelationship;
              const hasInsuredPartyRelationship =
                typeof insuredPartyRelationship === 'string' &&
                insuredPartyRelationship.trim().length > 0;
              const isInsuredPartySelf =
                hasInsuredPartyRelationship &&
                insuredPartyRelationship.toLowerCase() ===
                  StaticValues.RELATIONSHIP_SELF.toLowerCase();
              let primaryInsuranceHolder =
                incomingPrimaryInsuranceDetails?.isHolder;
              if (hasInsuredPartyRelationship) {
                primaryInsuranceHolder = isInsuredPartySelf
                  ? StaticValues.YES
                  : StaticValues.NO;
              }

              if (newData.insurance.plan === StaticValues.NO) {
                // Risposta "no": insurance non deve finire nel payload.
                // Svuoto primary, secondary e dati derivati.
                patientToUpdate.insurance = {
                  ...patientToUpdate.insurance,
                  ...newData.insurance,
                  plan: StaticValues.NO,
                  id: '',
                  other: '',
                  type: undefined,
                  name: undefined,
                  memberId: undefined,
                  holder: undefined,
                  secondaryInsurance: undefined,
                };

                if (patientToUpdate.patientDetails) {
                  patientToUpdate.patientDetails = {
                    ...patientToUpdate.patientDetails,
                    primaryInsuranceDetails: {
                      submitInsurancePlan: StaticValues.NO,
                    },
                    secondaryInsuranceDetails: undefined,
                    storeCustomFieldsInsurance: undefined,
                    customFieldsInsurance: [],
                  };
                }
              } else {
                // Risposta "yes" o carrier selezionato: questo ramo aggiorna
                // sempre la primary e conserva una secondary gia salvata.
                patientToUpdate.insurance = {
                  ...existingInsurance,
                  ...newData.insurance,
                  plan:
                    newData.insurance.plan ||
                    existingInsurance.plan ||
                    (primaryInsuranceId ? StaticValues.YES : ''),
                  id: primaryInsuranceId,
                  other: primaryInsuranceOther,
                  type: insuranceType,
                  name:
                    getInsuranceName(insurances, primaryInsuranceId) ||
                    newData.insurance.name ||
                    existingInsurance.name,
                  memberId: primaryInsuranceMemberId || undefined,
                  holder: existingInsurance.holder,
                  secondaryInsurance: existingInsurance.secondaryInsurance,
                };

                // Mantengo allineato anche patientDetails.primary:
                // il form insurance usa questi valori come base al render.
                if (!patientToUpdate.patientDetails && primaryInsuranceId) {
                  (patientToUpdate.patientDetails as any) = {};
                }
                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  primaryInsuranceDetails: {
                    ...incomingPrimaryInsuranceDetails,
                    insuranceId: primaryInsuranceId,
                    insuranceMemberId:
                      primaryInsuranceMemberId ||
                      incomingPrimaryInsuranceDetails?.insuranceMemberId,
                    insuranceNameOther:
                      primaryInsuranceOther ||
                      incomingPrimaryInsuranceDetails?.insuranceNameOther,
                    other:
                      primaryInsuranceOther ||
                      incomingPrimaryInsuranceDetails?.other,
                    submitInsurancePlan:
                      newData.insurance.plan ||
                      incomingPrimaryInsuranceDetails?.submitInsurancePlan ||
                      existingInsurance.plan,
                    isHolder: primaryInsuranceHolder,
                  },
                };
              }

              console.log('#new Data', newData.patientDetails);
            } else if (isInsuranceDetailsSection) {
              // Flusso PatientDetails: i form primary/secondary salvano i dettagli.
              // Da qui ricostruisco patient.insurance e secondaryInsurance.
              const patientData =
                patientToUpdate.patientDetails as PatientDetailsInfo;
              const incomingPatientData = newData.patientDetails as
                | Partial<PatientDetailsInfo>
                | undefined;
              const hasSecondaryInsuranceDetailsUpdate =
                !!incomingPatientData &&
                Object.hasOwn(
                  incomingPatientData,
                  'secondaryInsuranceDetails',
                );
              const isSecondaryInsurancePlanNo =
                hasSecondaryInsuranceDetailsUpdate &&
                incomingPatientData.secondaryInsuranceDetails === undefined;
              const existingInsurance = patientToUpdate.insurance || {
                plan: '',
              };

              console.log('#existingInsurance: ', existingInsurance);
              if (existingInsurance.plan === StaticValues.NO) {
                // Il radio plan puo stare anche in PatientDetails.
                // Se vale "no", questa sezione deve solo pulire tutto.
                patientToUpdate.insurance = {
                  ...existingInsurance,
                  plan: StaticValues.NO,
                  id: '',
                  other: '',
                  type: undefined,
                  name: undefined,
                  memberId: undefined,
                  holder: undefined,
                  secondaryInsurance: undefined,
                };

                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  primaryInsuranceDetails: {
                    submitInsurancePlan: StaticValues.NO,
                  },
                  secondaryInsuranceDetails: undefined,
                  storeCustomFieldsInsurance: undefined,
                  customFieldsInsurance: [],
                };
              } else {
                // Primary e secondary ora sono esplicite:
                // nessuna distinzione legacy decide piu la posizione.
                const primaryDetails = patientData?.primaryInsuranceDetails;
                const secondaryDetails = patientData?.secondaryInsuranceDetails;
                const primaryInsuranceId =
                  primaryDetails?.insuranceId || existingInsurance.id || '';
                const secondaryInsuranceId =
                  secondaryDetails?.insuranceId || '';
                const primarySubmitInsurancePlan =
                  existingInsurance.plan ||
                  primaryDetails?.submitInsurancePlan ||
                  (primaryInsuranceId ? StaticValues.YES : '');
                const hasSecondaryInsurance =
                  !!patientData?.secondaryInsuranceDetails ||
                  !!existingInsurance.secondaryInsurance?.id;
                let secondarySubmitInsurancePlan: string | undefined;
                if (existingInsurance.plan === StaticValues.YES) {
                  secondarySubmitInsurancePlan = isSecondaryInsurancePlanNo
                    ? StaticValues.NO
                    : secondaryDetails?.submitInsurancePlan;

                  if (!secondarySubmitInsurancePlan && hasSecondaryInsurance) {
                    secondarySubmitInsurancePlan = StaticValues.YES;
                  }
                }
                const primaryInsuranceType =
                  getInsuranceType(primaryInsuranceId) ||
                  existingInsurance.type;
                const shouldUsePrimaryHolderDetails =
                  shouldUseHolderDetails(primaryDetails);
                const shouldUseSecondaryHolderDetails =
                  shouldUseHolderDetails(secondaryDetails);
                const shouldUsePatientAsPrimaryHolder =
                  shouldUsePrimaryHolderDetails &&
                  primaryDetails?.isHolder === StaticValues.YES;
                let primaryHolderFallback: string | undefined;
                if (shouldUsePatientAsPrimaryHolder) {
                  primaryHolderFallback = getPatientName();
                }

                let primaryHolderName = existingInsurance.holder || '';
                if (primaryDetails) {
                  primaryHolderName = '';
                  if (shouldUsePrimaryHolderDetails) {
                    primaryHolderName = getHolderName(
                      primaryDetails,
                      primaryHolderFallback,
                    );
                  }
                }

                const shouldUsePrimaryAsSecondaryHolder =
                  shouldUseSecondaryHolderDetails &&
                  secondaryDetails?.sameAsForPrimaryInsurance &&
                  primaryDetails?.isHolder === StaticValues.NO;
                let secondaryFallbackHolder: string | undefined;
                if (shouldUsePrimaryAsSecondaryHolder) {
                  secondaryFallbackHolder = primaryHolderName;
                } else if (
                  shouldUseSecondaryHolderDetails &&
                  secondaryDetails?.isHolder === StaticValues.YES
                ) {
                  secondaryFallbackHolder = getPatientName();
                }

                let secondaryHolderName =
                  existingInsurance.secondaryInsurance?.holder || '';
                if (secondaryDetails) {
                  secondaryHolderName = '';
                  if (shouldUseSecondaryHolderDetails) {
                    secondaryHolderName = shouldUsePrimaryAsSecondaryHolder
                      ? primaryHolderName
                      : getHolderName(secondaryDetails, secondaryFallbackHolder);
                  }
                }

                // Ricostruisco il nodo usato dal payload save appointment.
                // La secondary viene inclusa solo se esiste anche una primary.
                patientToUpdate.insurance = {
                  ...existingInsurance,
                  plan:
                    existingInsurance.plan ||
                    (primaryInsuranceId ? StaticValues.YES : ''),
                  id: primaryInsuranceId,
                  other: getOtherInsurance(
                    primaryDetails,
                    primaryInsuranceId,
                    existingInsurance.other,
                  ),
                  type: primaryInsuranceType,
                  name:
                    getInsuranceName(insurances, primaryInsuranceId) ||
                    existingInsurance.name,
                  memberId: getInsuranceMemberId(
                    primaryDetails,
                    existingInsurance.memberId,
                  ),
                  holder: primaryHolderName,
                  secondaryInsurance:
                    primaryInsuranceId && secondaryInsuranceId
                      ? {
                          id: secondaryInsuranceId,
                          memberId: getInsuranceMemberId(
                            secondaryDetails,
                            existingInsurance.secondaryInsurance?.memberId,
                          ),
                          holder: secondaryHolderName,
                          name:
                            getInsuranceName(
                              insurances,
                              secondaryInsuranceId,
                            ) || existingInsurance.secondaryInsurance?.name,
                          type:
                            getInsuranceType(secondaryInsuranceId) ||
                            existingInsurance.secondaryInsurance?.type,
                          other: getOtherInsurance(
                            secondaryDetails,
                            secondaryInsuranceId,
                            existingInsurance.secondaryInsurance?.other,
                          ),
                        }
                      : undefined,
                };
                console.log('Log Store 1');
                // Mantengo i nuovi dettagli e azzero i vecchi rami legacy.
                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  primaryInsuranceDetails: primarySubmitInsurancePlan
                    ? {
                        ...primaryDetails,
                        submitInsurancePlan: primarySubmitInsurancePlan,
                      }
                    : primaryDetails,
                  secondaryInsuranceDetails: secondarySubmitInsurancePlan
                    ? {
                        ...secondaryDetails,
                        submitInsurancePlan: secondarySubmitInsurancePlan,
                      }
                    : secondaryDetails,
                };
              }
            }
          }

          // Aggiorna l'array dei pazienti con il paziente aggiornato
          tempState.patients[currentPatient] = patientToUpdate;

          // Restituisce il nuovo stato con l'array dei pazienti aggiornato
          return { ...tempState };
        });
      },

      deletePatient: (patientIndex: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;

          // Fai una copia dell'array dei pazienti, evitando di modificarlo direttamente
          // const updatedPatients = [...tempState.patients];
          tempState.patients[patientIndex] = undefined;
          // updatedPatients.splice(patientIndex, 1);

          // // Aggiorna lo stato con la nuova lista
          // tempState.patients = updatedPatients;

          // const remainingPatientsCount = updatedPatients.length;
          const onlyTruePatient = tempState.patients.filter(p => p);
          console.log('#deletePatient.onlyTruePatient: ', onlyTruePatient);
          if (onlyTruePatient.length > 0) {
            const findLastIndex = tempState.patients.findLastIndex(p => p);
            if (findLastIndex !== -1) {
              useOnlineBookingStore.getState().setCurrentPatient(findLastIndex);
            } else {
              console.log('## non ci sono più pazienti...');
            }
          } else {
            console.log('## non ci sono più pazienti...');
          }

          // if (remainingPatientsCount > 0) {
          //   const newCurrentPatientIndex = Math.min(
          //     patientIndex,
          //     remainingPatientsCount - 1,
          //   );
          //   useOnlineBookingStore
          //     .getState()
          //     .setCurrentPatient(newCurrentPatientIndex);
          // } else {
          //   useOnlineBookingStore.getState().setCurrentPatient(0);
          // }

          // step 0 utente non creato... => situazione attuale [u,u,u]
          // step 1 utente aggiunto (seleziono appt) => situazione attuale [u,u,u] situazione post [0,u,u]
          // order = 0 -> posizione [0]
          // step 2 utente aggiunto (seleziono appt) => situazione attuale [0,u,u] situazione post [0,1,u]
          // order = 0 -> posizione [0]
          // order = 1 -> posizione [1]
          // step 3 utente cancellato posizione 0 => situazione attuale [0,1,u] situazione post [u,1,u]
          // order = 1 -> posizione [1]
          // step 4 utente aggiunto => situazione attuale [u,1,u] situazione post [0,1,u]
          // mi prendo l'order più grande (della situazione attuale) ... 1, al nuovo utente l'order è ((più grnade) + 1)
          // logica di "spazio libero" più a sinistra => situazione attuale [u,1,u] allora lo "spazio" libero è 0
          // order = 2 -> posizione [0]
          // order = 1 -> posizione [1]

          return { ...tempState };
        });
      },

      importPatientDetailsData: () => {
        set(state => {
          // TODO RIVEDERE
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          const patients = tempState.patients;
          const patientToImport = patients.find(
            p =>
              p?.order ===
              Math.min(
                ...patients.map(obj =>
                  typeof obj?.order !== 'undefined' ? obj.order : 99,
                ),
              ),
          );

          return {
            ...tempState,
            contactsDetails: {
              ...tempState.contactsDetails,
              firstName: patientToImport?.patientDetails?.firstName,
              lastName: patientToImport?.patientDetails?.lastName,
              phone: patientToImport?.patientDetails?.phone,
              email: patientToImport?.patientDetails?.email,
              dateOfBirth: patientToImport?.patientDetails?.dateOfBirth,
            },
          };
          // return { ...tempState };
        });
      },

      setShowDeletePatientModal: (show: boolean) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          tempState.showDeletePatientModal = show;
          return { ...tempState };
        });
      },

      getCurrentPatient: (currentPatient: number) => {
        return get().patients[currentPatient];
      },

      // Funzione per resettare lo stato
      reset: () => set(initialState),
    }),
    {
      name: 'patientProgression',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
